import { FloatingAlertList } from 'foundations-components/transitional/components/FloatingAlertList';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Raven from 'raven-js';
import { useEffect, useMemo, useState } from 'react';
import { Flow } from 'signup-constants/Flow';
import { Intent } from 'signup-constants/Intent';
import { useCreateDirectToBuyConfigMutation } from '../../../../__generated__/chirp/com/hubspot/buying/rpc/PublicDirectToBuyRpc.hooks';
import { useGlobalAlertStore } from '../../../hooks/useGlobalAlertStore';
import { useSignupContext } from '../../../hooks/useSignupContext';
import { incrementDirectToBuyConfigFailure, incrementDirectToBuyConfigSuccess } from '../../../utils/metrics';
import { getDtbSource, getTrackingSourceFromTrackingData } from '../../../utils/productIdentifier';
import { isAvailableCurrencyCode } from '../../../utils/purchaseMotionTypeGuards';
import { generateSignupUrlWithQueryParams } from '../../../utils/signupUrl';
import { navigateTo } from '../../../utils/urls';
import { ErrorBoundary, ErrorBoundaryNames } from '../../ErrorBoundary';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DirectToBuyButtonBase = props => {
  const {
    'data-test-id': dataTestId,
    'data-selenium-id': dataSeleniumId,
    apiName,
    ctaType = 'buy',
    children,
    eligibleTreatments,
    uiButtonProps,
    onClick,
    onView,
    trackingData,
    additionalQueryParams,
    browserLocale,
    preferredCountryCode
  } = props;
  const trackingSource = getTrackingSourceFromTrackingData(trackingData);
  const dtbSource = getDtbSource(trackingData, trackingSource, apiName);
  useEffect(() => {
    onView === null || onView === void 0 || onView();
  }, [onView]);
  const purchaseMotion = 'BUY_TOUCHLESS';
  const purchaseRequest = useMemo(() => {
    if ('purchaseRequest' in props && props.purchaseRequest) {
      return props.purchaseRequest;
    }
    if ('currencyCode' in props && props.currencyCode) {
      var _props$terms, _props$terms2, _props$terms3, _props$terms4;
      return Object.assign({
        products: [{
          productApiName: apiName,
          incrementalQuantity: 1
        }],
        quantityPacks: [],
        currencyCode: props.currencyCode
      }, (_props$terms = props.terms) !== null && _props$terms !== void 0 && _props$terms.commitmentTerm && (_props$terms2 = props.terms) !== null && _props$terms2 !== void 0 && _props$terms2.paymentTerm ? {
        commitmentTerm: (_props$terms3 = props.terms) === null || _props$terms3 === void 0 ? void 0 : _props$terms3.commitmentTerm,
        paymentTerm: (_props$terms4 = props.terms) === null || _props$terms4 === void 0 ? void 0 : _props$terms4.paymentTerm
      } : {});
    }
    throw new Error('DirectToBuyButton requires either purchaseRequest or currencyCode');
  }, [apiName, props]);
  const currencyCode = isAvailableCurrencyCode(purchaseRequest.currencyCode) ? purchaseRequest.currencyCode : undefined;
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const {
    alertStore,
    shouldRenderGlobalList
  } = useGlobalAlertStore();
  const [createDirectToBuyConfig] = useCreateDirectToBuyConfigMutation();
  const apiNames = useMemo(() => {
    var _purchaseRequest$prod, _purchaseRequest$quan;
    return Array.from(new Set([...(((_purchaseRequest$prod = purchaseRequest.products) === null || _purchaseRequest$prod === void 0 ? void 0 : _purchaseRequest$prod.map(p => p.productApiName)) || []), ...(((_purchaseRequest$quan = purchaseRequest.quantityPacks) === null || _purchaseRequest$quan === void 0 ? void 0 : _purchaseRequest$quan.map(p => p.parentProductApiName)) || [])]));
  }, [purchaseRequest]);
  const {
    hubsSignupUrl,
    hubsSignupCta
  } = useSignupContext({
    apiName,
    ctaType,
    trackingData,
    productSku: props.productSku
  });
  const offerTerms = useMemo(() => {
    const commitment = purchaseRequest.commitmentTerm;
    const payment = purchaseRequest.paymentTerm;
    if (commitment === 'MONTHLY' && payment === 'MONTHLY') {
      return ['MONTHLY_COMMIT_MONTHLY_PAY'];
    }
    if (commitment === 'ANNUAL' && payment === 'MONTHLY') {
      return ['ANNUAL_COMMIT_MONTHLY_PAY'];
    }
    if (commitment === 'ANNUAL' && payment === 'ANNUAL') {
      return ['ANNUAL_COMMIT_ANNUAL_PAY'];
    }
    return [];
  }, [purchaseRequest]);
  const handleClick = async () => {
    if (loading) return;
    setFailed(false);
    setLoading(true);
    onClick === null || onClick === void 0 || onClick(purchaseMotion);
    try {
      var _purchaseRequest$prod2, _purchaseRequest$quan2, _response$data;
      const productConfigurations = [];
      (_purchaseRequest$prod2 = purchaseRequest.products) === null || _purchaseRequest$prod2 === void 0 || _purchaseRequest$prod2.forEach(product => {
        productConfigurations.push({
          productApiName: product.productApiName,
          quantity: product.skuQuantity || product.incrementalQuantity || 1
        });
      });
      (_purchaseRequest$quan2 = purchaseRequest.quantityPacks) === null || _purchaseRequest$quan2 === void 0 || _purchaseRequest$quan2.forEach(pack => {
        productConfigurations.push({
          productApiName: pack.parentProductApiName,
          quantityName: pack.quantityName,
          quantity: pack.skuQuantity || pack.incrementalQuantity || 1
        });
      });
      if (productConfigurations.length === 0) {
        throw new Error('No product configurations in purchase request');
      }
      const response = await createDirectToBuyConfig({
        variables: {
          productConfigurations,
          browserLocale,
          preferredCountryCode,
          currencyCode,
          terms: offerTerms
        },
        context: {
          timeout: 20000
        }
      });
      const dtbId = (_response$data = response.data) === null || _response$data === void 0 || (_response$data = _response$data.result) === null || _response$data === void 0 ? void 0 : _response$data.id;
      if (!dtbId) {
        throw new Error('No DTB ID returned from createDirectToBuyConfig');
      }
      const signupUrl = generateSignupUrlWithQueryParams({
        signupType: Flow.Purchase,
        signupIntent: Intent.purchase,
        queryParams: {
          dtbId: dtbId.toString(),
          currencyCode,
          hubsSignupUrl,
          domainWithoutHttps: window.location.hostname.replace(/^https?:\/\//, ''),
          hubsSignupCta,
          pathname: window.location.pathname,
          uuid: window.__utAnonId || '',
          dtbSource
        },
        additionalQueryParams,
        experiments: eligibleTreatments
      });
      incrementDirectToBuyConfigSuccess(apiNames);
      navigateTo(signupUrl);
    } catch (error) {
      setFailed(true);
      incrementDirectToBuyConfigFailure(apiNames);
      alertStore.addAlert({
        id: 'direct-to-buy-failure',
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "purchaseMotions.directToBuyFailureAlert.title"
        }),
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "purchaseMotions.directToBuyFailureAlert.message"
        }),
        type: 'danger'
      });
      Raven.captureMessage('Failed to create DTB Config', {
        extra: {
          error,
          apiNames,
          browserLocale,
          currencyCode: purchaseRequest.currencyCode,
          offerTerms
        },
        fingerprint: ['public-direct-to-buy-button-failure']
      });
    } finally {
      setLoading(false);
    }
  };
  const buttonText = children || /*#__PURE__*/_jsx(FormattedMessage, {
    message: "purchaseMotions.buttons.buyTouchless"
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [shouldRenderGlobalList && /*#__PURE__*/_jsx(FloatingAlertList, {
      "data-test-id": "dtb-floating-alert-list",
      alertStore: alertStore
    }), /*#__PURE__*/_jsx(LoadingButton, Object.assign({
      "data-test-id": dataTestId !== null && dataTestId !== void 0 ? dataTestId : 'buy-now-button',
      "data-selenium-id": dataSeleniumId,
      use: "primary",
      truncate: true,
      truncateStringProps: {
        justifyContent: 'center'
      }
    }, uiButtonProps, {
      loading: loading,
      failed: failed,
      onClick: () => {
        void handleClick();
      },
      children: buttonText
    }))]
  });
};
export const DirectToBuyButton = props => /*#__PURE__*/_jsx(ErrorBoundary, {
  boundaryName: ErrorBoundaryNames.BUYING_BUTTON,
  children: /*#__PURE__*/_jsx(DirectToBuyButtonBase, Object.assign({}, props))
});